import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  AppDispatch,
  convertPathToDotNotation,
  sanitize,
  setAddressData,
  setSelectedCountry,
} from '@msc-b2c/shared-utils';
import { keyCodes } from '@msc-b2c/shared-utils/keyboardUtils';

export type BaseSelectProps = {
  label: string;
  placeholder: string;
  ariaLabel: string;
  items: { value: string; text: string; showHide?: string; enableDisable?: string }[];
  errors: any;
  fieldName: string;
  disabled?: boolean;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  clearErrors: any;
  allowPlaceholderSelection?: boolean;
  hidePlaceholder?: boolean; // When true, hides the placeholder option entirely
  useStandardUI?: boolean; // Toggle between custom and native select
  styleOption?: string;
  icon?: string; // Custom icon class name (defaults to 'icon-down')
};

export const BaseSelect = ({
  label,
  placeholder,
  ariaLabel,
  items,
  fieldName,
  disabled,
  value,
  onChange,
  onBlur,
  errors,
  clearErrors,
  allowPlaceholderSelection = false,
  hidePlaceholder = false,
  useStandardUI = false,
  styleOption,
  icon,
}: BaseSelectProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const selectRef = useRef<HTMLDivElement>(null);
  const [selectedValue, setSelectedValue] = useState(value || ''); // Set default as empty string
  useEffect(() => {
    // Sync selectedValue with the value prop if it changes
    if (value !== selectedValue) {
      setSelectedValue(value || '');
    }
  }, [value]);

  // TODO; Find better way to manage country/zipCode combination - START;
  useEffect(() => {
    if (fieldName !== 'address.country' && fieldName !== 'address.countryCode') {
      return;
    }
    dispatch(setSelectedCountry(selectedValue));
    dispatch(setAddressData([]));

    return () => {
      if (fieldName !== 'address.country' && fieldName !== 'address.countryCode') {
        return;
      }
      dispatch(setSelectedCountry(''));
    };
  }, [fieldName, selectedValue]);
  // TODO; Find better way to manage country/zipCode combination - END;
  const handleToggleDropdown = (
    event?: React.MouseEvent | React.TouchEvent | React.KeyboardEvent
  ) => {
    event?.preventDefault();
    if (disabled) return;

    const selectElement = selectRef.current?.querySelector('select') as HTMLSelectElement;
    if (selectElement) {
      selectElement.focus({ preventScroll: true });
      // Use modern showPicker API if available, otherwise fallback to click simulation
      if ('showPicker' in selectElement && typeof selectElement.showPicker === 'function') {
        try {
          selectElement.showPicker();
        } catch (error) {
          // Fallback if showPicker fails
          selectElement.focus();
          selectElement.click();
        }
      } else {
        // Fallback for older browsers
        // Create and dispatch a synthetic click event
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: globalThis as unknown as Window,
        });
        selectElement.dispatchEvent(clickEvent);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    // Handle spacebar and enter key
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleToggleDropdown(event);
    }
    // Handle escape key to close dropdown
    else if (event.key === 'Escape') {
      event.preventDefault();
      const selectElement = selectRef.current?.querySelector('select') as HTMLSelectElement;
      if (selectElement) {
        selectElement.blur();
      }
    }
  };

  const openNativeSelect = (select: HTMLSelectElement) => {
    if (typeof select.showPicker === 'function') {
      try {
        select.showPicker();
        return;
      } catch {
        // showPicker may throw when not allowed; fall back to click activation.
      }
    }

    select.click();
  };

  const hasError = Boolean(convertPathToDotNotation(errors, fieldName));

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptionValue = event.target.value;
    setSelectedValue(selectedOptionValue);
    onChange(selectedOptionValue); // Update the value
    clearErrors(fieldName);
    if (onBlur) {
      onBlur();
    }
  };

  const handleNativeSelectKeyDown = (event: React.KeyboardEvent<HTMLSelectElement>) => {
    if (disabled || event.key !== keyCodes.ENTER) {
      return;
    }

    event.preventDefault();
    openNativeSelect(event.currentTarget);
  };

  return (
    <div
      className={`cmp-select__upperlabel ${styleOption} ${disabled ? 'disabled-select' : ''} ${
        hasError ? 'error-select' : ''
      }`}
    >
      <fieldset
        className="cmp-form-options cmp-form-options--drop-down"
        role="group"
      >
        <label
          className="cmp-form-options__label"
          htmlFor={fieldName}
        >
          {label}
        </label>

        <div
          className={'custom-select'}
          ref={selectRef}
        >
          <button
            className="select-button"
            role="combobox"
            aria-label={ariaLabel}
            aria-haspopup="listbox"
            aria-controls={`${fieldName}-select-dropdown`}
            name={fieldName}
            onClick={handleToggleDropdown}
            onMouseDown={handleToggleDropdown}
            onTouchStart={handleToggleDropdown}
            onFocus={() => clearErrors(fieldName)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            type="button"
            tabIndex={0}
          >
            <span
              className={`selected-value ${selectedValue === '' ? 'is-placeholder' : ''}`}
              dangerouslySetInnerHTML={{
                __html: sanitize(
                  selectedValue
                    ? items?.find((item) => item.value === selectedValue)?.text ?? ''
                    : placeholder
                ),
              }}
            ></span>
            <i className={`icon ${icon ? `icon-${icon}` : 'icon-down'}`}></i>
          </button>
          <select
            className="standard-ui-select"
            name={fieldName}
            id={`${fieldName}-select-dropdown`}
            value={selectedValue}
            onChange={handleSelectChange}
            onKeyDown={handleNativeSelectKeyDown}
            onFocus={() => clearErrors(fieldName)}
            onBlur={onBlur}
            disabled={disabled}
            aria-label={ariaLabel}
            role="listbox"
            tabIndex={-1}
          >
            {!hidePlaceholder && (
              <option
                value=""
                disabled={!allowPlaceholderSelection}
              >
                {placeholder}
              </option>
            )}
            {items?.map((item) => (
              <option
                key={item.value}
                value={item.value}
                dangerouslySetInnerHTML={{ __html: sanitize(item.text) }}
              ></option>
            ))}
          </select>
        </div>
      </fieldset>
      {hasError && (
        <div className="error-list">
          <div className="vr-mandatory-field">
            {convertPathToDotNotation(errors, fieldName)?.message?.toString()}
          </div>
        </div>
      )}
    </div>
  );
};
